import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  GridLegacy as Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  EmojiEvents as TrophyIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as PrizeIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import FeatureGuard from '../components/FeatureGuard';

interface MonthData {
  monthKey: string;
  lastUpdated: string;
  totalTraders: number;
}

const CompetitionPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [competitionData, setCompetitionData] = useState<any>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [availableMonths, setAvailableMonths] = useState<MonthData[]>([]);
  const [monthsLoading, setMonthsLoading] = useState(true);

  // Initialize Firebase callable functions
  const getAvailableLeaderboardMonths = httpsCallable(functions, 'getAvailableLeaderboardMonths');
  const getLeaderboardByMonth = httpsCallable(functions, 'getLeaderboardByMonth');

  // Fetch available months on component mount
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      try {
        setMonthsLoading(true);
        const result = await getAvailableLeaderboardMonths();
        const response = result.data as { success: boolean; months: MonthData[] };
        
        if (response.success && response.months) {
          setAvailableMonths(response.months);
          
          // Set current month as default if no month is selected
          if (!selectedMonth) {
            const currentMonth = new Date().toISOString().slice(0, 7);
            const hasCurrentMonth = response.months.some(month => month.monthKey === currentMonth);
            setSelectedMonth(hasCurrentMonth ? currentMonth : response.months[0]?.monthKey || currentMonth);
          }
        }
      } catch (error) {
        console.error('Error fetching available months:', error);
      } finally {
        setMonthsLoading(false);
      }
    };

    fetchAvailableMonths();
  }, []);

  // Fetch leaderboard data when selectedMonth changes
  useEffect(() => {
    if (!selectedMonth) return;

    const fetchCompetitionData = async () => {
      try {
        setLoading(true);
        
        // Use Firebase function to get leaderboard data
        const result = await getLeaderboardByMonth({ monthKey: selectedMonth });
        const response = result.data as { 
          success: boolean; 
          data: any[]; 
          monthKey: string; 
          lastUpdated: string; 
          totalTraders: number;
        };
        
        if (response.success) {
          setLeaderboard(response.data || []);
          setCompetitionData({
            leaderboard: response.data || [],
            lastUpdated: response.lastUpdated,
            totalTraders: response.totalTraders,
            monthKey: response.monthKey
          });
        } else {
          // Fallback to direct Firestore query for current month
          const leaderboardDoc = await getDoc(doc(db, 'leaderboardData', selectedMonth));
          
          if (leaderboardDoc.exists()) {
            const data = leaderboardDoc.data();
            setLeaderboard(data.leaderboard || []);
            setCompetitionData(data);
          } else {
            setLeaderboard([]);
            setCompetitionData(null);
          }
        }
      } catch (error) {
        console.error('Error fetching competition data:', error);
        // Fallback to direct Firestore query
        try {
          const leaderboardDoc = await getDoc(doc(db, 'leaderboardData', selectedMonth));
          
          if (leaderboardDoc.exists()) {
            const data = leaderboardDoc.data();
            setLeaderboard(data.leaderboard || []);
            setCompetitionData(data);
          } else {
            setLeaderboard([]);
            setCompetitionData(null);
          }
        } catch (fallbackError) {
          console.error('Fallback query also failed:', fallbackError);
          setLeaderboard([]);
          setCompetitionData(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitionData();
  }, [selectedMonth]);

  const handleMonthChange = (event: SelectChangeEvent<string>) => {
    setSelectedMonth(event.target.value);
  };

  const formatMonthDisplay = (monthKey: string) => {
    const date = new Date(monthKey + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isCurrentMonth = () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    return selectedMonth === currentMonth;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  const getRankColor = (rank: number) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return theme.palette.text.secondary;
    }
  };

  const instrumentData = [
    {
      name: 'Gold',
      symbol: 'GOLD',
      points: 1,
      color: '#FFD700',
      icon: '🥇',
      description: 'Most traded precious metal'
    },
    {
      name: 'Bitcoin',
      symbol: 'BTCUSD',
      points: 2,
      color: '#FF6B35',
      icon: '₿',
      description: 'Leading cryptocurrency'
    },
    {
      name: 'Silver',
      symbol: 'SILVER',
      points: 3,
      color: '#C0C0C0',
      icon: '🥈',
      description: 'Premium precious metal'
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', pt: { xs: 12, md: 14 }, pb: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <TrophyIcon sx={{ fontSize: 60, color: '#FFD700', mb: 2 }} />
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #FFD700 30%, #FFA000 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Volume Masters Competition
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {selectedMonth ? formatMonthDisplay(selectedMonth) : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Typography>
              <Chip
                icon={<PrizeIcon />}
                label="Prize Pool: R50,000"
                sx={{
                  backgroundColor: 'rgba(255, 215, 0, 0.2)',
                  color: '#FFD700',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  px: 2,
                  py: 1,
                  height: 'auto',
                  minHeight: 40,
                }}
              />
            </Box>
          </motion.div>

          {/* Month Selection */}
          <motion.div variants={itemVariants}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel id="month-select-label">
                  <HistoryIcon sx={{ mr: 1, fontSize: 20 }} />
                  Select Competition Month
                </InputLabel>
                <Select
                  labelId="month-select-label"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  label="Select Competition Month"
                  disabled={monthsLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: 'rgba(255, 215, 0, 0.3)',
                      '&:hover': {
                        borderColor: 'rgba(255, 215, 0, 0.5)',
                      },
                      '&.Mui-focused': {
                        borderColor: '#FFD700',
                      },
                    },
                  }}
                >
                  {availableMonths.map((month) => (
                    <MenuItem key={month.monthKey} value={month.monthKey}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography>
                          {formatMonthDisplay(month.monthKey)}
                        </Typography>
                        {month.monthKey === new Date().toISOString().slice(0, 7) && (
                          <Chip 
                            label="Current" 
                            size="small" 
                            sx={{ 
                              backgroundColor: 'rgba(76, 175, 80, 0.2)', 
                              color: '#4CAF50',
                              fontSize: '0.7rem',
                              ml: 1 
                            }} 
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </motion.div>

          {/* Competition Info Cards */}
          <motion.div variants={itemVariants}>
            <Grid container spacing={3} sx={{ mb: 6 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <CalendarIcon sx={{ fontSize: 40, color: '#FFD700', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Competition Period
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monthly Competition
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      1st - 31st of each month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TimelineIcon sx={{ fontSize: 40, color: '#FFD700', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      How to Participate
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Trade Gold, Bitcoin, or Silver
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Points calculated automatically
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ height: '100%', bgcolor: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TrophyIcon sx={{ fontSize: 40, color: '#FFD700', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      Winner Selection
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Highest total points wins
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Monthly reset & new competition
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </motion.div>

          {/* Points Calculation System */}
          <motion.div variants={itemVariants}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                mb: 6,
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 160, 0, 0.05) 100%)',
                border: '2px solid rgba(255, 215, 0, 0.3)',
                borderRadius: 3,
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <StarIcon sx={{ fontSize: 40, color: '#FFD700', mb: 2 }} />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#FFD700' }}>
                  Points Calculation System
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Best overall trader determined by points accumulated on XM Global
                </Typography>
              </Box>

              {/* Instrument Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {instrumentData.map((instrument, index) => (
                  <Grid item xs={12} md={4} key={instrument.symbol}>
                    <Card
                      sx={{
                        height: '100%',
                        background: `linear-gradient(135deg, ${instrument.color}20 0%, ${instrument.color}10 100%)`,
                        border: `2px solid ${instrument.color}40`,
                        borderRadius: 3,
                        transition: 'transform 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <Typography variant="h2" sx={{ mb: 1 }}>
                          {instrument.icon}
                        </Typography>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: instrument.color }}>
                          {instrument.name}
                        </Typography>
                        <Typography variant="h3" sx={{ fontWeight: 700, color: instrument.color, mb: 1 }}>
                          {instrument.points} Point{instrument.points > 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          per lot traded
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {instrument.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Formula */}
              <Box
                sx={{
                  textAlign: 'center',
                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                  border: '2px solid rgba(33, 150, 243, 0.3)',
                  borderRadius: 3,
                  p: 3,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#2196F3' }}>
                  Formula: Total Points = Instrument Multiplier × Total Lots Traded
                </Typography>
              </Box>
            </Paper>
          </motion.div>

          {/* Leaderboard */}
          <motion.div variants={itemVariants}>
            <Paper elevation={2} sx={{ mb: 4 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrophyIcon sx={{ color: '#FFD700' }} />
                  {isCurrentMonth() ? 'Current Leaderboard' : 'Historical Results'}
                  {!isCurrentMonth() && (
                    <Chip 
                      label="Final Results" 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(156, 39, 176, 0.2)', 
                        color: '#9C27B0',
                        ml: 1 
                      }} 
                    />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isCurrentMonth() ? 'Live rankings' : 'Final results'} for {selectedMonth ? formatMonthDisplay(selectedMonth) : new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  {competitionData?.lastUpdated && (
                    <> • Last updated: {new Date(competitionData.lastUpdated).toLocaleDateString()}</>
                  )}
                </Typography>
              </Box>

              {monthsLoading || loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                  <CircularProgress sx={{ color: '#FFD700', mb: 2 }} />
                  <Typography color="text.secondary">
                    {monthsLoading ? 'Loading available months...' : 'Loading competition data...'}
                  </Typography>
                </Box>
              ) : leaderboard.length > 0 ? (
                <>
                  {/* Winner Highlight for Historical Data */}
                  {!isCurrentMonth() && leaderboard.length > 0 && (
                    <Box sx={{ p: 3, bgcolor: 'rgba(255, 215, 0, 0.1)', borderBottom: '1px solid rgba(255, 215, 0, 0.2)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <TrophyIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                            🏆 WINNER: {leaderboard[0].displayName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {leaderboard[0].totalPoints.toLocaleString()} points • R50,000 Prize
                          </Typography>
                        </Box>
                        <TrophyIcon sx={{ color: '#FFD700', fontSize: 32 }} />
                      </Box>
                    </Box>
                  )}
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Rank</strong></TableCell>
                          <TableCell><strong>Trader</strong></TableCell>
                          <TableCell align="right"><strong>Total Points</strong></TableCell>
                          <TableCell align="right"><strong>Gold Lots</strong></TableCell>
                          <TableCell align="right"><strong>BTC Lots</strong></TableCell>
                          <TableCell align="right"><strong>Silver Lots</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leaderboard.slice(0, 10).map((trader, index) => (
                          <TableRow
                            key={trader.loginId}
                            sx={{
                              bgcolor: index < 3 ? `${getRankColor(index + 1)}10` : 'transparent',
                              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                              ...(index === 0 && !isCurrentMonth() && {
                                bgcolor: 'rgba(255, 215, 0, 0.15)',
                                '&:hover': { bgcolor: 'rgba(255, 215, 0, 0.2)' },
                              }),
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: getRankColor(index + 1),
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                #{index + 1}
                                {index === 0 && !isCurrentMonth() && (
                                  <Chip 
                                    label="WINNER" 
                                    size="small" 
                                    sx={{ 
                                      backgroundColor: '#FFD700', 
                                      color: '#000',
                                      fontWeight: 'bold',
                                      fontSize: '0.6rem',
                                    }} 
                                  />
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {trader.displayName}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                                {trader.totalPoints.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{trader.goldLots.toFixed(2)}</TableCell>
                            <TableCell align="right">{trader.btcLots.toFixed(2)}</TableCell>
                            <TableCell align="right">{trader.silverLots.toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No competition data available
                  </Typography>
                  <Typography color="text.secondary">
                    {selectedMonth ? 
                      `No trading activity recorded for ${formatMonthDisplay(selectedMonth)}.` :
                      'No competition data available for the selected month.'
                    }
                  </Typography>
                  {!isCurrentMonth() && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      This may be a month before the competition started.
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </motion.div>

          {/* Competition Rules */}
          <motion.div variants={itemVariants}>
            <Paper elevation={2} sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Competition Rules & Terms
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#FFD700' }}>
                    Eligibility
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Must have verified XM account with T1 FX Team
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Only Standard account types are eligible
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Must be an active T1 FX Team member
                  </Typography>

                  <Typography variant="h6" gutterBottom sx={{ color: '#FFD700', mt: 3 }}>
                    Eligible Instruments
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Gold: GOLD
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Bitcoin: BTCUSD
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Silver: SILVER
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#FFD700' }}>
                    Prize Distribution
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • 1st Place: R50,000 (Winner takes all)
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Monthly competition with fresh start each month
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Prize paid within 30 days of month end
                  </Typography>

                  <Typography variant="h6" gutterBottom sx={{ color: '#FFD700', mt: 3 }}>
                    Important Notes
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Points calculated automatically from trading data
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • Live leaderboard updates daily
                  </Typography>
                  <Typography variant="body2" paragraph>
                    • All trades must be completed within the month
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3, borderColor: 'rgba(255, 215, 0, 0.2)' }} />

              <Alert severity="info" sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                <Typography variant="body2">
                  <strong>Disclaimer:</strong> This competition is for T1 FX Team members only. 
                  All trading involves risk, and past performance does not guarantee future results. 
                  Please trade responsibly and within your risk tolerance.
                </Typography>
              </Alert>
            </Paper>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

function CompetitionFeature() {
  return (
    <FeatureGuard featureId="competitions">
      <CompetitionPage />
    </FeatureGuard>
  );
}

export const Competition = CompetitionFeature;

export default CompetitionFeature; 